// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import useReprocessAttestations from '../../../hooks/mutations/admin/useReprocessAttestations';
import useMutate from '../../../hooks/mutations/useMutate';

interface DialogProps {
  open: boolean;
  onClose: () => void;
}

export const ReprocessAttestationsDialog = ({ open, onClose }: DialogProps) => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [batchSize, setBatchSize] = useState('');
  const [results, setResults] = useState<string[] | null>(null);
  const reprocessMutation = useReprocessAttestations();
  const mutate = useMutate();

  const handleSubmit = async () => {
    const result = await mutate(
      reprocessMutation,
      {
        startTime: new Date(startTime).toISOString(),
        endTime: new Date(endTime).toISOString(),
        ...(batchSize ? { batchSize: parseInt(batchSize, 10) } : {}),
      },
      'Attestations reprocessed successfully',
      'Failed to reprocess attestations',
    );
    if (result) {
      setResults(result);
    }
  };

  const isValid = startTime !== '' && endTime !== '';

  const handleClose = () => {
    setStartTime('');
    setEndTime('');
    setBatchSize('');
    setResults(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reprocess Deposit Attestations</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Start Time"
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Time"
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Batch Size (optional, default: 20)"
            type="number"
            value={batchSize}
            onChange={(e) => setBatchSize(e.target.value)}
            fullWidth
          />
          {results && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Results ({results.length} contract{results.length !== 1 ? 's' : ''}):
              </Typography>
              <Box
                sx={{
                  maxHeight: 200,
                  overflow: 'auto',
                  bgcolor: 'grey.100',
                  p: 1,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                }}
              >
                {results.map((r, i) => (
                  <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {r}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || reprocessMutation.isPending}
        >
          Reprocess
        </Button>
      </DialogActions>
    </Dialog>
  );
};
